import React, { useEffect, useMemo, useRef, useState } from "react";
import { useWatch, useFormContext, Controller } from "react-hook-form";
import {
  Grid,
  TextField,
  Typography,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

import InputAdornment from "@mui/material/InputAdornment";
import { NumericFormat } from "react-number-format";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { handleNumberInputKeyDown } from "../../utils/common";
import { useQuery } from "@tanstack/react-query";
import {
  getFinancialDefaultData,
  updateLinkage,
} from "../../store/modules/transactions/transactionActions";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { LinkFieldEnumModel } from "../../types/types";
import LoadingDialog from "../Dialogs/LoadingDialog";
import { get } from "lodash";
import { isPipelineStatus } from "../../hooks/usePipeline";
import { findAnniversaryForDate } from "../../utils/transactionFinancials";

interface FinancialDetailsProps {
  readOnly?: boolean;
  isEdit?: boolean;
  isFinancialTab?: boolean;
  transactionId?: number | null;
  // data?: any; // Should be properly typed based on transaction financials
}

export default function FinancialDetails({
  readOnly = false,
  isEdit = false,
  transactionId,
  isFinancialTab = false,
}: // data,
FinancialDetailsProps) {
  const formContext = useFormContext();

  const { userData } = useSelector((state: RootState) => state.commonData);
  const [isSaving, setIsSaving] = useState(false);
  const { data: brokerageAnniversaries } = useSelector(
    (state: RootState) => state.financialSettings,
  ).brokerageAnniversaries;

  const control = formContext?.control;
  const currentAnniversary = useWatch({
    control,
    name: "brkAnniversary",
  });

  // Get source information to check if it's Agent Referral
  const selectedSource = useWatch({
    control,
    name: "source",
  }) as number | undefined;
  const isAgentReferral =
    selectedSource &&
    userData?.data?.sources?.find((x) => x.id === selectedSource)?.name ===
      "Agent Referral";

  // The edit form is populated by a single methods.reset(), which moves source
  // from undefined to its saved value. That transition is not a user edit, so
  // only a later change latches this flag.
  const prevSourceRef = useRef<number | null | undefined>(undefined);
  const [sourceUserChanged, setSourceUserChanged] = useState(false);

  useEffect(() => {
    const prev = prevSourceRef.current;
    prevSourceRef.current = selectedSource;
    if (prev !== undefined && prev !== selectedSource) {
      setSourceUserChanged(true);
    }
  }, [selectedSource]);

  if (!transactionId && formContext.getValues("id")) {
    transactionId = formContext.getValues("id");
  }
  const { data: prefillData } = useQuery({
    queryKey: [
      "fetchFinancialDefaultData",
      transactionId,
      formContext.getValues("date"),
    ],
    refetchOnWindowFocus: false,
    queryFn: () =>
      getFinancialDefaultData(transactionId!, formContext.getValues("date")),
    enabled: isFinancialTab && transactionId !== null,
  });

  const closingDate = useWatch({ control, name: "date" });
  const selectedStatus = useWatch({ control, name: "status" }) as
    | number
    | undefined;

  // The brokerage anniversary that applies to this transaction's closing date.
  // Looked up locally by day, so it doesn't depend on how the server's
  // anniversary date and the Redux list's dates were serialized.
  const matchingAnniversary = useMemo(() => {
    if (!prefillData || !brokerageAnniversaries?.length) return undefined;
    return (
      findAnniversaryForDate(brokerageAnniversaries, closingDate) ??
      brokerageAnniversaries.find(
        (anniversary) =>
          anniversary.anniversaryDate &&
          prefillData.brokerageAnniversaryDate &&
          dayjs(anniversary.anniversaryDate).isSame(
            dayjs(prefillData.brokerageAnniversaryDate),
            "day",
          ),
      )
    );
  }, [brokerageAnniversaries, prefillData, closingDate]);

  // State for tracking link/unlink status for each field
  const [fieldLinkStatus, setFieldLinkStatus] = useState<{
    commissionPercentage?: boolean;
    flatCommission?: boolean;
    brokerageSplit?: boolean;
    additionalCharges?: boolean;
    brkFlatFees?: boolean;
    referralFeeReceived?: boolean;
    referralFeePaid?: boolean;
  }>({
    commissionPercentage: undefined,
    flatCommission: undefined,
    brokerageSplit: undefined,
    additionalCharges: undefined,
    brkFlatFees: undefined,
    referralFeeReceived: undefined,
    referralFeePaid: undefined,
  });

  // Function to toggle link status for a field
  const toggleFieldLink = async (fieldName: LinkFieldEnumModel) => {
    const newLinkStatus = !fieldLinkStatus[getFieldKey(fieldName)];
    setFieldLinkStatus((prev) => ({
      ...prev,
      [getFieldKey(fieldName)]: newLinkStatus,
    }));

    // Call the updateLinkage action directly
    // Fall back to the prop for new transactions where form "id" is not yet set
    const txId = formContext.getValues("id") || transactionId;
    if (txId) {
      try {
        setIsSaving(true);
        await updateLinkage(txId, fieldName, newLinkStatus);
        //if linked, update the form value to the anniversary default if available
        if (newLinkStatus && brokerageAnniversaries && currentAnniversary) {
          const linkedAnniversary =
            brokerageAnniversaries.find((anniversary) =>
              dayjs(anniversary.anniversaryDate).isSame(
                currentAnniversary,
                "day",
              ),
            ) ?? matchingAnniversary;
          if (linkedAnniversary) {
            if (fieldName === LinkFieldEnumModel.CommissionPct) {
              formContext.setValue(
                getFieldKey(fieldName),
                linkedAnniversary.commissionPercentage,
                {
                  shouldDirty: true,
                },
              );
            } else if (fieldName === LinkFieldEnumModel.FlatCommissionFee) {
              // Flat Commission is never pushed to Referral Out transactions
              const currentTypeId = formContext.getValues("transactionType");
              const txIsReferralOut =
                (currentTypeId &&
                  userData?.data?.transactionTypes?.find(
                    (x) => x.id === currentTypeId,
                  )?.showReferralOut) ??
                false;
              if (!txIsReferralOut) {
                formContext.setValue(
                  getFieldKey(fieldName),
                  linkedAnniversary.flatCommissionFee,
                  {
                    shouldDirty: true,
                  },
                );
              }
            } else if (fieldName === LinkFieldEnumModel.BrokerageSplit) {
              formContext.setValue(
                getFieldKey(fieldName),
                linkedAnniversary.brokerageSplit,
                {
                  shouldDirty: true,
                },
              );
            } else if (fieldName === LinkFieldEnumModel.AdditionalCharges) {
              formContext.setValue(
                getFieldKey(fieldName),
                linkedAnniversary.additionalChargePercentage,
                {
                  shouldDirty: true,
                },
              );
            } else if (fieldName === LinkFieldEnumModel.BrokerageFlatFees) {
              formContext.setValue(
                getFieldKey(fieldName),
                linkedAnniversary.flatTransactionalFees,
                {
                  shouldDirty: true,
                },
              );
            } else if (
              fieldName === LinkFieldEnumModel.ReferralOutFeeReceived
            ) {
              formContext.setValue(
                getFieldKey(fieldName),
                linkedAnniversary.referralOutFeeReceived,
                {
                  shouldDirty: true,
                },
              );
            } else if (fieldName === LinkFieldEnumModel.ReferralFeePaid) {
              formContext.setValue(
                getFieldKey(fieldName),
                linkedAnniversary.referralFeePaid,
                {
                  shouldDirty: true,
                },
              );
            }
          }
        }
      } catch (error) {
        console.error("Failed to update linkage:", error);
        // Revert the UI state if the API call fails
        setFieldLinkStatus((prev) => ({
          ...prev,
          [getFieldKey(fieldName)]: !newLinkStatus,
        }));
      } finally {
        setIsSaving(false);
      }
    }
  };

  const getFieldKey = (
    fieldName: LinkFieldEnumModel,
  ): keyof typeof fieldLinkStatus => {
    switch (fieldName) {
      case LinkFieldEnumModel.CommissionPct:
        return "commissionPercentage";
      case LinkFieldEnumModel.FlatCommissionFee:
        return "flatCommission";
      case LinkFieldEnumModel.BrokerageSplit:
        return "brokerageSplit";
      case LinkFieldEnumModel.AdditionalCharges:
        return "additionalCharges";
      case LinkFieldEnumModel.BrokerageFlatFees:
        return "brkFlatFees";
      case LinkFieldEnumModel.ReferralOutFeeReceived:
        return "referralFeeReceived";
      case LinkFieldEnumModel.ReferralFeePaid:
        return "referralFeePaid";
      default:
        return "commissionPercentage";
    }
  };

  // Update form values when prefill data is available
  useEffect(() => {
    if (prefillData) {
      setFieldLinkStatus({
        commissionPercentage: !prefillData?.commissionPctUnlinked,
        flatCommission: !prefillData?.flatCommissionFeeUnlinked,
        brokerageSplit: !prefillData?.brokerageSplitUnlinked,
        additionalCharges: !prefillData?.additionalChargesUnlinked,
        brkFlatFees: !prefillData?.brokerageFlatFeesUnlinked,
        referralFeeReceived: !prefillData?.referralOutFeeReceivedUnlinked,
        referralFeePaid: !prefillData?.referralFeePaidUnlinked,
      });
      // Get the current value of brkAnniversary
      // const currentAnniversary = formContext.getValues("brkAnniversary");

      // Only update values if brkAnniversary is not set
      if (!currentAnniversary && prefillData.brokerageAnniversaryDate) {
        formContext.setValue(
          "brkAnniversary",
          prefillData.brokerageAnniversaryDate!,
          { shouldDirty: true },
        );
      }
    }
  }, [prefillData]);

  // Add mode, pipeline entries (Lead/Active/Under Contract): prefill the linked fields from
  // the brokerage anniversary. The server never applies the anniversary to pipeline entries,
  // and they never count toward the caps, so the full percentages apply. A new Closed
  // entry gets the server's cap-adjusted values instead (loaded by TransactionAdd).
  const isPipelineEntry = isPipelineStatus(selectedStatus);
  const prefilledKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      isEdit ||
      !formContext ||
      !isPipelineEntry ||
      !prefillData ||
      !matchingAnniversary
    ) {
      return;
    }

    // Once per entry and anniversary, so later renders don't reset what the user typed
    const prefillKey = `${transactionId}-${matchingAnniversary.brokerageAnniversaryId}`;
    if (prefilledKeyRef.current === prefillKey) return;
    prefilledKeyRef.current = prefillKey;

    const typeId = formContext.getValues("transactionType");
    const transactionType = userData?.data?.transactionTypes?.find(
      (x) => x.id === typeId,
    );
    const isReferralOut = transactionType?.showReferralOut ?? false;

    // Same rules as [dbo].[ApplyFinancialSettingsToTransactions]
    const defaults: Record<string, { linked: boolean; value: number }> = {
      commissionPercentage: {
        linked: !prefillData.commissionPctUnlinked,
        value: transactionType?.isMonthlyRent
          ? (matchingAnniversary.rentalCommissionPercentage ?? 50)
          : matchingAnniversary.commissionPercentage || 0,
      },
      brokerageSplit: {
        linked: !prefillData.brokerageSplitUnlinked,
        value: matchingAnniversary.brokerageSplit || 0,
      },
      additionalCharges: {
        linked: !prefillData.additionalChargesUnlinked,
        value: matchingAnniversary.additionalChargePercentage || 0,
      },
      brkFlatFees: {
        linked: !prefillData.brokerageFlatFeesUnlinked,
        value: matchingAnniversary.flatTransactionalFees || 0,
      },
      // Flat Commission is never pushed to Referral Out transactions
      flatCommission: {
        linked: !prefillData.flatCommissionFeeUnlinked && !isReferralOut,
        value: matchingAnniversary.flatCommissionFee || 0,
      },
      referralFeeReceived: {
        linked: !prefillData.referralOutFeeReceivedUnlinked && isReferralOut,
        value: matchingAnniversary.referralOutFeeReceived || 0,
      },
      // Referral Fee Paid only applies to Agent Referral sources
      referralFeePaid: {
        linked: !prefillData.referralFeePaidUnlinked,
        value: isAgentReferral ? matchingAnniversary.referralFeePaid || 0 : 0,
      },
    };

    Object.entries(defaults).forEach(([field, { linked, value }]) => {
      if (linked) {
        formContext.setValue(field, value, { shouldDirty: false });
      }
    });
  }, [
    formContext,
    isEdit,
    isPipelineEntry,
    prefillData,
    matchingAnniversary,
    transactionId,
    isAgentReferral,
    userData,
  ]);

  // Edit mode and new pipeline entries: re-evaluate Referral Fee Paid when the
  // user changes Source. Deliberately narrow - it touches only this field, so
  // values for the other anniversary defaults are never rewritten.
  useEffect(() => {
    if (
      (!isEdit && !isPipelineEntry) ||
      !formContext ||
      !sourceUserChanged ||
      !prefillData ||
      // undefined = prefill not loaded yet, false = manually unlinked by the user
      fieldLinkStatus.referralFeePaid !== true
    ) {
      return;
    }

    formContext.setValue(
      "referralFeePaid",
      isAgentReferral ? matchingAnniversary?.referralFeePaid || 0 : 0,
      { shouldDirty: true },
    );
  }, [
    isEdit,
    isPipelineEntry,
    formContext,
    sourceUserChanged,
    prefillData,
    fieldLinkStatus.referralFeePaid,
    isAgentReferral,
    matchingAnniversary,
  ]);

  const price = useWatch({ control, name: "price" });
  const commissionPercentage = useWatch({
    control,
    name: "commissionPercentage",
  });
  const referralFeePaid = useWatch({ control, name: "referralFeePaid" });
  const coAgentSplit = useWatch({ control, name: "coAgentSplit" });
  const coAgentSplit2 = useWatch({ control, name: "coAgentSplit2" });

  const referralFeeReceived = useWatch({
    control,
    name: "referralFeeReceived",
  });
  const incentivesPaid = useWatch({ control, name: "incentivesPaid" });
  const brokerageSplit = useWatch({ control, name: "brokerageSplit" });
  const brokerageAdditionalCharge = useWatch({
    control,
    name: "additionalCharges",
  });
  const brokerageFlatFees = useWatch({ control, name: "brkFlatFees" });
  const flatCommissionFee = useWatch({ control, name: "flatCommission" });

  // Get transaction type information
  const selectedType = useWatch({
    control,
    name: "transactionType",
  }) as number | undefined;
  const isMonthlyRent =
    (selectedType &&
      userData?.data?.transactionTypes?.find((x) => x.id === selectedType)
        ?.isMonthlyRent) ??
    false;
  const isReferralOut =
    (selectedType &&
      userData?.data?.transactionTypes?.find((x) => x.id === selectedType)
        ?.showReferralOut) ??
    false;

  // Calculate derived values
  const grossCommissionIncome =
    price && commissionPercentage ? price * (commissionPercentage / 100) : 0;
  const clientRebatePrice = useWatch({ control, name: "clientRebatePrice" });
  const clientRebateGCI = useWatch({ control, name: "clientRebateGCI" });
  const clientRebateFlatAmount = useWatch({
    control,
    name: "clientRebateFlatAmount",
  });

  const totalClientRebate =
    ((Number(clientRebatePrice) || 0) * price) / 100 +
    ((Number(clientRebateGCI) || 0) * grossCommissionIncome) / 100 +
    (Number(clientRebateFlatAmount) || 0);

  const referralFeePaidAmount =
    ((!isReferralOut ? referralFeePaid || 0 : 0) *
      ((grossCommissionIncome || 0) - totalClientRebate)) /
    100;

  const referralFeeReceivedAmount =
    ((isReferralOut ? referralFeeReceived || 0 : 0) / 100) *
    ((grossCommissionIncome || 0) - totalClientRebate);

  // Calculate base amount for co-agent splits (matches SQL CalculateEffectiveGCI logic)
  const baseForCoAgentSplits = isReferralOut
    ? referralFeeReceivedAmount
    : grossCommissionIncome -
      totalClientRebate -
      referralFeePaidAmount +
      referralFeeReceivedAmount;

  const coAgentFee = ((Number(coAgentSplit) || 0) / 100) * baseForCoAgentSplits;

  const coAgent2Fee =
    ((Number(coAgentSplit2) || 0) / 100) * baseForCoAgentSplits;

  //The Effective GCI is calculated with co-agent splits deducted for both transaction types
  const effectiveGCI = isReferralOut
    ? referralFeeReceivedAmount - coAgentFee - coAgent2Fee
    : grossCommissionIncome -
      totalClientRebate -
      referralFeePaidAmount +
      referralFeeReceivedAmount -
      coAgentFee -
      coAgent2Fee;

  // Brokerage calculations
  // ((field.value || 0) / 100) * effectiveGCI
  const brokerageSplitAmount =
    (effectiveGCI * (Number(brokerageSplit) || 0)) / 100;
  // ((field.value || 0) / 100) * effectiveGCI)
  const brokerageAdditionalAmount =
    (effectiveGCI * (Number(brokerageAdditionalCharge) || 0)) / 100;
  const brokerageTotalFees =
    brokerageSplitAmount +
    brokerageAdditionalAmount +
    (Number(brokerageFlatFees) || 0);
  //Effective GCI – Brokerage Total Fees + Flat Commission/Fee
  const netCommissionIncome =
    effectiveGCI - brokerageTotalFees + (Number(flatCommissionFee) || 0);

  // Final net calculations

  const netIncome = netCommissionIncome - (Number(incentivesPaid) || 0);

  //Net Income *100 / Price
  const netIncomePercentage = price ? (netIncome / price) * 100 : 0;

  return (
    <Grid container spacing={3}>
      <LoadingDialog open={isSaving} />
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          Core Transaction
        </Typography>
      </Grid>
      <Grid item xs={12} md={3}>
        <Controller
          name="price"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <NumericFormat
              fullWidth
              id="price"
              valueIsNumericString
              onValueChange={(values) => field.onChange(values.floatValue)}
              customInput={TextField}
              thousandSeparator
              allowLeadingZeros={false}
              decimalScale={0}
              onBlur={field.onBlur}
              // onChange={field.onChange}
              // value={field.value || 0}
              value={field.value === "" ? 0 : field.value}
              label={
                <span>
                  {isMonthlyRent ? "Monthly Rent" : "Price"}
                  <span style={{ color: "red" }}>*</span>
                </span>
              }
              allowNegative={false}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                },
              }}
              inputProps={{
                maxLength: 50,
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <Controller
          name="commissionPercentage"
          control={control}
          rules={{
            min: { value: 0, message: "Minimum 0%" },
            max: { value: 100, message: "Maximum 100%" },
          }}
          render={({ field }) => (
            <TextField
              id="commissionPercentage"
              type="number"
              label="Commission Percentage"
              onKeyDown={handleNumberInputKeyDown}
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              fullWidth
              sx={
                fieldLinkStatus.commissionPercentage // && field.value //&&
                  ? //!formContext?.formState?.dirtyFields?.commissionPercentage &&
                    //!isEdit
                    {
                      "& .MuiInputBase-root": {
                        backgroundColor: "#e6f3ff",
                      },
                    }
                  : {}
              }
              {...field}
              value={field.value ?? 0}
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 100.0,
                  step: 0.1,
                },

                input: {
                  readOnly: fieldLinkStatus.commissionPercentage === true,
                  startAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <Tooltip
                        title={
                          fieldLinkStatus.commissionPercentage
                            ? "Linked to anniversary defaults"
                            : "Unlinked - manual entry only"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            toggleFieldLink(LinkFieldEnumModel.CommissionPct)
                          }
                          sx={{
                            minWidth: "32px",
                            minHeight: "32px",
                            padding: "4px",
                          }}
                        >
                          {fieldLinkStatus.commissionPercentage ? (
                            <LinkIcon
                              color="primary"
                              sx={{ fontSize: "18px" }}
                            />
                          ) : (
                            <LinkOffIcon
                              color="action"
                              sx={{ fontSize: "18px" }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <Controller
          name="grossCommissionIncome"
          control={control}
          render={({ field }) => (
            <TextField
              id="grossCommissionIncome"
              onKeyDown={handleNumberInputKeyDown}
              type="number"
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              label="Gross Commission Income"
              fullWidth
              helperText="(auto-calculated and can't be modified)"
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "#fff9e6",
                },
              }}
              // value={grossCommissionIncome?.toFixed(2) ?? ""}
              // value={
              //   typeof grossCommissionIncome === "number" &&
              //   !isNaN(grossCommissionIncome)
              //     ? grossCommissionIncome.toFixed(2)
              //     : ""
              // }
              value={(grossCommissionIncome || 0).toFixed(2)}
              slotProps={{
                input: {
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <Controller
          name="flatCommission"
          control={control}
          render={({ field }) => (
            <TextField
              label="Flat Commission"
              onKeyDown={handleNumberInputKeyDown}
              type="number"
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              fullWidth
              sx={
                fieldLinkStatus.flatCommission //&& field.value //&&
                  ? // !formContext?.formState?.dirtyFields?.flatCommission &&
                    //!isEdit
                    {
                      "& .MuiInputBase-root": {
                        backgroundColor: "#e6f3ff",
                      },
                    }
                  : {}
              }
              {...field}
              value={field.value ?? 0}
              slotProps={{
                input: {
                  readOnly: fieldLinkStatus.flatCommission === true,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <Tooltip
                        title={
                          fieldLinkStatus.flatCommission
                            ? "Linked to anniversary defaults"
                            : "Unlinked - manual entry only"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            toggleFieldLink(
                              LinkFieldEnumModel.FlatCommissionFee,
                            )
                          }
                          sx={{
                            minWidth: "32px",
                            minHeight: "32px",
                            padding: "4px",
                          }}
                        >
                          {fieldLinkStatus.flatCommission ? (
                            <LinkIcon
                              color="primary"
                              sx={{ fontSize: "18px" }}
                            />
                          ) : (
                            <LinkOffIcon
                              color="action"
                              sx={{ fontSize: "18px" }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>

      {/* Client Rebate Fields */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          Client Rebate
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        {/* <FormControl fullWidth variant="filled">
            <InputLabel htmlFor="clientRebatePrice">
              Client Rebate Price
            </InputLabel> */}
        <Controller
          name="clientRebatePrice"
          control={control}
          rules={{
            min: { value: 0, message: "Minimum 0%" },
            max: { value: 100, message: "Maximum 100%" },
          }}
          render={({ field }) => (
            <TextField
              id="clientRebatePrice"
              onKeyDown={handleNumberInputKeyDown}
              type="number"
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              label="Client Rebate (% of Price)"
              fullWidth
              {...field}
              value={field.value ?? 0}
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 100.0,
                  step: 0.1,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        padding: "10px",
                        backgroundColor: "#fff9e6",
                        borderTopLeftRadius: (theme) => "10px",
                        borderBottomLeftRadius: (theme) => "10px",
                        borderTopRightRadius: (theme) => "10px",
                        borderBottomRightRadius: (theme) => "10px",
                      }}
                    >
                      {`$${(((field.value || 0) * price) / 100).toFixed(2)}`}
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="clientRebateGCI"
          rules={{
            min: { value: 0, message: "Minimum 0%" },
            max: { value: 100, message: "Maximum 100%" },
          }}
          control={control}
          render={({ field }) => (
            <TextField
              id="clientRebateGCI"
              onKeyDown={handleNumberInputKeyDown}
              // defaultValue={0}
              label="Client Rebate (% of GCI)"
              fullWidth
              type="number"
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              {...field}
              value={field.value ?? 0}
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 100.0,
                  step: 0.1,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      sx={{
                        padding: "10px",
                        backgroundColor: "#fff9e6",
                        borderTopLeftRadius: (theme) => "10px",
                        borderBottomLeftRadius: (theme) => "10px",
                        borderTopRightRadius: (theme) => "10px",
                        borderBottomRightRadius: (theme) => "10px",
                      }}
                      position="end"
                      // sx={{
                      //   "& .MuiInputAdornment-positionEnd": {
                      //     backgroundColor: "#fff9e6",
                      //   },
                      // }}
                    >
                      {`$${(
                        ((field.value || 0) * (grossCommissionIncome || 0)) /
                        100
                      ).toFixed(2)}`}
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="clientRebateFlatAmount"
          control={control}
          render={({ field }) => (
            <TextField
              id="clientRebateFlatAmount"
              onKeyDown={handleNumberInputKeyDown}
              type="number"
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              label="Flat Amount"
              fullWidth
              {...field}
              value={field.value ?? 0}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>

      {/* Referral Fees */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          Referral and Co-Agent Fees
        </Typography>
      </Grid>

      {!isReferralOut && (
        <Grid item xs={12} md={4}>
          {/* Box next to entered value shows the calculated $ = (GCI - $ Client Rebates all three
                added) * % value entered */}
          <Controller
            name="referralFeePaid"
            control={control}
            rules={{
              min: { value: 0, message: "Minimum 0%" },
              max: { value: 100, message: "Maximum 100%" },
            }}
            render={({ field }) => (
              <TextField
                id="referralFeePaid"
                onKeyDown={handleNumberInputKeyDown}
                label="Referral Fee Paid"
                fullWidth
                type="number"
                onWheel={(e) =>
                  e.target instanceof HTMLElement && e.target.blur()
                }
                sx={
                  isAgentReferral === true && fieldLinkStatus.referralFeePaid
                    ? {
                        "& .MuiInputBase-root": {
                          backgroundColor: "#e6f3ff",
                        },
                      }
                    : {}
                }
                {...field}
                value={field.value ?? 0}
                slotProps={{
                  htmlInput: {
                    min: 0,
                    max: 100.0,
                    step: 0.1,
                  },
                  input: {
                    readOnly:
                      isAgentReferral === true &&
                      fieldLinkStatus.referralFeePaid === true,
                    startAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),

                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <span
                          style={{
                            padding: "10px",
                            backgroundColor: "#fff9e6",
                            borderTopLeftRadius: "10px",
                            borderBottomLeftRadius: "10px",
                            borderTopRightRadius: "10px",
                            borderBottomRightRadius: "10px",
                          }}
                        >
                          {`$${(
                            ((field.value || 0) *
                              ((grossCommissionIncome || 0) -
                                totalClientRebate)) /
                            100
                          ).toFixed(2)}`}
                        </span>
                        {isAgentReferral && (
                          <Tooltip
                            title={
                              fieldLinkStatus.referralFeePaid
                                ? "Linked to anniversary defaults"
                                : "Unlinked - manual entry only"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                toggleFieldLink(
                                  LinkFieldEnumModel.ReferralFeePaid,
                                )
                              }
                              sx={{
                                minWidth: "32px",
                                minHeight: "32px",
                                padding: "4px",
                              }}
                            >
                              {fieldLinkStatus.referralFeePaid ? (
                                <LinkIcon
                                  color="primary"
                                  sx={{ fontSize: "18px" }}
                                />
                              ) : (
                                <LinkOffIcon
                                  color="action"
                                  sx={{ fontSize: "18px" }}
                                />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        </Grid>
      )}
      <Grid item xs={12} md={4}>
        {/* $ = (GCI – Client Rebate - $ Referral
        Fee Paid + Referral Fee Received) * % value entered */}
        <Controller
          name="coAgentSplit"
          rules={{
            min: { value: 0, message: "Minimum 0%" },
            max: { value: 100, message: "Maximum 100%" },
          }}
          control={control}
          render={({ field }) => (
            <TextField
              id="coAgentSplit"
              type="number"
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              fullWidth
              onKeyDown={handleNumberInputKeyDown}
              label="Team Member/Co-Agent Split Paid"
              {...field}
              value={field.value ?? 0}
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 100.0,
                  step: 0.1,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        padding: "10px",
                        backgroundColor: "#fff9e6",
                        borderTopLeftRadius: (theme) => "10px",
                        borderBottomLeftRadius: (theme) => "10px",
                        borderTopRightRadius: (theme) => "10px",
                        borderBottomRightRadius: (theme) => "10px",
                      }}
                    >
                      {`$${(
                        ((field.value || 0) / 100) *
                        ((grossCommissionIncome || 0) -
                          totalClientRebate -
                          referralFeePaidAmount +
                          referralFeeReceivedAmount)
                      ).toFixed(2)}`}
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        {/* $ = (GCI – Client Rebate - $ Referral
        Fee Paid + Referral Fee Received) * % value entered */}
        <Controller
          name="coAgentSplit2"
          rules={{
            min: { value: 0, message: "Minimum 0%" },
            max: { value: 100, message: "Maximum 100%" },
          }}
          control={control}
          render={({ field }) => (
            <TextField
              id="coAgentSplit2"
              type="number"
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              fullWidth
              onKeyDown={handleNumberInputKeyDown}
              label="Team Member/Co-Agent 2 Split Paid"
              {...field}
              value={field.value ?? 0}
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 100.0,
                  step: 0.1,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        padding: "10px",
                        backgroundColor: "#fff9e6",
                        borderTopLeftRadius: (theme) => "10px",
                        borderBottomLeftRadius: (theme) => "10px",
                        borderTopRightRadius: (theme) => "10px",
                        borderBottomRightRadius: (theme) => "10px",
                      }}
                    >
                      {`$${(
                        ((field.value || 0) / 100) *
                        ((grossCommissionIncome || 0) -
                          totalClientRebate -
                          referralFeePaidAmount +
                          referralFeeReceivedAmount)
                      ).toFixed(2)}`}
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
      {isReferralOut && (
        <Grid item xs={12} md={4}>
          {/* (GCI – Client Rebate) * % value */}
          <Controller
            name="referralFeeReceived"
            rules={{
              min: { value: 0, message: "Minimum 0%" },
              max: { value: 100, message: "Maximum 100%" },
            }}
            control={control}
            render={({ field }) => (
              <TextField
                id="referralFeeReceived"
                onKeyDown={handleNumberInputKeyDown}
                type="number"
                onWheel={(e) =>
                  e.target instanceof HTMLElement && e.target.blur()
                }
                label="Referral Fee Received"
                fullWidth
                {...field}
                value={field.value ?? 0}
                slotProps={{
                  htmlInput: {
                    min: 0,
                    max: 100.0,
                    step: 0.1,
                  },
                  input: {
                    readOnly: fieldLinkStatus.referralFeeReceived === true,
                    startAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <span
                          style={{
                            padding: "10px",
                            backgroundColor: "#fff9e6",
                            borderTopLeftRadius: "10px",
                            borderBottomLeftRadius: "10px",
                            borderTopRightRadius: "10px",
                            borderBottomRightRadius: "10px",
                          }}
                        >
                          {`$${(
                            ((field.value || 0) / 100) *
                            ((grossCommissionIncome || 0) - totalClientRebate)
                          ).toFixed(2)}`}
                        </span>
                        <Tooltip
                          title={
                            fieldLinkStatus.referralFeeReceived
                              ? "Linked to anniversary defaults"
                              : "Unlinked - manual entry only"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              toggleFieldLink(
                                LinkFieldEnumModel.ReferralOutFeeReceived,
                              )
                            }
                            sx={{
                              minWidth: "32px",
                              minHeight: "32px",
                              padding: "4px",
                            }}
                          >
                            {fieldLinkStatus.referralFeeReceived ? (
                              <LinkIcon
                                color="primary"
                                sx={{ fontSize: "18px" }}
                              />
                            ) : (
                              <LinkOffIcon
                                color="action"
                                sx={{ fontSize: "18px" }}
                              />
                            )}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                    // endAdornment: (
                    //   <InputAdornment
                    //     position="end"
                    //     sx={{
                    //       padding: "10px",
                    //       backgroundColor: "#fff9e6",
                    //       borderTopLeftRadius: (theme) => "10px",
                    //       borderBottomLeftRadius: (theme) => "10px",
                    //       borderTopRightRadius: (theme) => "10px",
                    //       borderBottomRightRadius: (theme) => "10px",
                    //     }}
                    //   >
                    //     {`$${(
                    //       ((field.value || 0) / 100) *
                    //       ((grossCommissionIncome || 0) - totalClientRebate)
                    //     ).toFixed(2)}`}
                    //   </InputAdornment>
                    // ),
                  },
                }}
              />
            )}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <Divider sx={{ my: 3 }} />
        <Grid item xs={12} md={4}>
          <Controller
            name="effectiveGCI"
            control={control}
            render={({ field }) => (
              <TextField
                type="number"
                onWheel={(e) =>
                  e.target instanceof HTMLElement && e.target.blur()
                }
                label="Effective GCI"
                helperText="(auto-calculated and can't be modified)"
                fullWidth
                // value={effectiveGCI?.toFixed(2) ?? ""}
                value={(effectiveGCI || 0).toFixed(2)}
                // value={
                //   typeof effectiveGCI === "number" && !isNaN(effectiveGCI)
                //     ? effectiveGCI.toFixed(2)
                //     : ""
                // }
                onKeyDown={handleNumberInputKeyDown}
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: "#fff9e6",
                  },
                }}
                slotProps={{
                  input: {
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        </Grid>
      </Grid>
      {/* Brokerage Details */}
      <Grid item xs={12}>
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" gutterBottom>
          Brokerage Fees
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="brkAnniversary" //brkAnniversary
          control={control}
          defaultValue={null}
          render={({ field }) => (
            <DatePicker
              label="Brokerage Anniversary Date"
              {...field}
              value={field.value} //? dayjs(field.value) : null
              onChange={(newValue) => field.onChange(newValue)}
              // format="MM/DD/YYYY"

              sx={{ width: "100%" }}
              slotProps={{
                textField: {
                  onBlur: field.onBlur,
                  sx:
                    field.value &&
                    !formContext?.formState?.dirtyFields?.brkAnniversary &&
                    !isEdit
                      ? {
                          "& .MuiInputBase-root": {
                            backgroundColor: "#e6f3ff",
                          },
                        }
                      : {},
                  fullWidth: true,
                  helperText:
                    field.value &&
                    !formContext?.formState?.dirtyFields?.brkAnniversary &&
                    !isEdit
                      ? ""
                      : "",
                  InputLabelProps: {
                    shrink: true,
                    sx: {
                      // backgroundColor: "background.paper",
                      px: 1,
                      // "& .MuiOutlinedInput-notchedOutline": {
                      //   border: "none",
                      // },
                    },
                  },
                },

                field: { onBlur: () => field.onBlur(), clearable: true },
                popper: {
                  // onClose: () => field.onBlur(),
                  disablePortal: true,
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        {/* Effective GCI * % value entered */}
        <Controller
          name="brokerageSplit"
          control={control}
          rules={{
            min: { value: 0, message: "Minimum 0%" },
            max: { value: 100, message: "Maximum 100%" },
            validate: (value) =>
              (value >= 0 && value <= 100) || "Must be between 0-100%",
          }}
          render={({ field }) => (
            <TextField
              id="brokerageSplit"
              type="number"
              // defaultValue={0}
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              onKeyDown={handleNumberInputKeyDown}
              fullWidth
              helperText={
                `Total Paid: $${(
                  formContext.getValues("runningSplitTotal") ?? 0
                ).toFixed(2)}`
                // (prefillData?.totalBrokerageSplit ?? 0) >= 0
                //   ? `Total Paid: ${
                //       prefillData?.totalBrokerageSplit?.toFixed(2) ?? 0
                //     } ${
                //       field.value &&
                //       !formContext?.formState?.dirtyFields?.brokerageSplit &&
                //       !isEdit
                //         ? ""
                //         : ""
                //     }`
                //   : field.value &&
                //     !formContext?.formState?.dirtyFields?.brokerageSplit &&
                //     !isEdit
                //   ? ""
                //   : ""
              }
              sx={
                fieldLinkStatus.brokerageSplit //&& field.value //&&
                  ? // !formContext?.formState?.dirtyFields?.brokerageSplit &&
                    // !isEdit
                    {
                      "& .MuiInputBase-root": {
                        backgroundColor: "#e6f3ff",
                      },
                    }
                  : {}
              }
              label="Brokerage Split"
              {...field}
              value={field.value ?? 0}
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 100.0,
                  step: 0.1,
                },
                input: {
                  readOnly: fieldLinkStatus.brokerageSplit === true,
                  startAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span
                        style={{
                          padding: "10px",
                          backgroundColor: "#fff9e6",
                          borderTopLeftRadius: "10px",
                          borderBottomLeftRadius: "10px",
                          borderTopRightRadius: "10px",
                          borderBottomRightRadius: "10px",
                        }}
                      >
                        {`$${(
                          ((field.value || 0) / 100) *
                          effectiveGCI
                        ).toFixed(2)}`}
                      </span>
                      <Tooltip
                        title={
                          fieldLinkStatus.brokerageSplit
                            ? "Linked to anniversary defaults"
                            : "Unlinked - manual entry only"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            toggleFieldLink(LinkFieldEnumModel.BrokerageSplit)
                          }
                          sx={{
                            minWidth: "32px",
                            minHeight: "32px",
                            padding: "4px",
                          }}
                        >
                          {fieldLinkStatus.brokerageSplit ? (
                            <LinkIcon
                              color="primary"
                              sx={{ fontSize: "18px" }}
                            />
                          ) : (
                            <LinkOffIcon
                              color="action"
                              sx={{ fontSize: "18px" }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        {/* Effective GCI * % value entered. */}
        <Controller
          name="additionalCharges"
          control={control}
          rules={{
            min: { value: 0, message: "Minimum 0%" },
            max: { value: 100, message: "Maximum 100%" },
          }}
          render={({ field }) => (
            <TextField
              id="additionalCharges"
              type="number"
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              label="Additional % Brokerage Charge"
              helperText={`Total Paid: $${(
                formContext.getValues("runningAdditionalChargeTotal") ?? 0
              ).toFixed(2)}`}
              sx={
                fieldLinkStatus.additionalCharges // && field.value //&&
                  ? //!formContext?.formState?.dirtyFields?.additionalCharges &&
                    //!isEdit
                    {
                      "& .MuiInputBase-root": {
                        backgroundColor: "#e6f3ff",
                      },
                    }
                  : {}
              }
              onKeyDown={handleNumberInputKeyDown}
              fullWidth
              {...field}
              value={field.value ?? 0}
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 100.0,
                  step: 0.1,
                },
                input: {
                  readOnly: fieldLinkStatus.additionalCharges === true,
                  startAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span
                        style={{
                          padding: "10px",
                          backgroundColor: "#fff9e6",
                          borderTopLeftRadius: "10px",
                          borderBottomLeftRadius: "10px",
                          borderTopRightRadius: "10px",
                          borderBottomRightRadius: "10px",
                        }}
                      >
                        {`$${(
                          ((field.value || 0) / 100) *
                          effectiveGCI
                        ).toFixed(2)}`}
                      </span>
                      <Tooltip
                        title={
                          fieldLinkStatus.additionalCharges
                            ? "Linked to anniversary defaults"
                            : "Unlinked - manual entry only"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            toggleFieldLink(
                              LinkFieldEnumModel.AdditionalCharges,
                            )
                          }
                          sx={{
                            minWidth: "32px",
                            minHeight: "32px",
                            padding: "4px",
                          }}
                        >
                          {fieldLinkStatus.additionalCharges ? (
                            <LinkIcon
                              color="primary"
                              sx={{ fontSize: "18px" }}
                            />
                          ) : (
                            <LinkOffIcon
                              color="action"
                              sx={{ fontSize: "18px" }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="brkFlatFees"
          control={control}
          render={({ field }) => (
            <TextField
              type="number"
              label="Brokerage Flat Fees"
              // helperText="(auto-calculated and can't be modified)"
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              onKeyDown={handleNumberInputKeyDown}
              fullWidth
              // sx={{
              //   "& .MuiInputBase-root": {
              //     backgroundColor: "#fff9e6",
              //   },
              // }}
              sx={
                fieldLinkStatus.brkFlatFees //&& field.value //&&
                  ? //!formContext?.formState?.dirtyFields?.brkFlatFees &&
                    // !isEdit
                    {
                      "& .MuiInputBase-root": {
                        backgroundColor: "#e6f3ff",
                      },
                    }
                  : {}
              }
              helperText={
                field.value &&
                !formContext?.formState?.dirtyFields?.brkFlatFees &&
                !isEdit
                  ? ""
                  : ""
              }
              {...field}
              value={field.value ?? 0}
              slotProps={{
                input: {
                  readOnly: fieldLinkStatus.brkFlatFees === true,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <Tooltip
                        title={
                          fieldLinkStatus.brkFlatFees
                            ? "Linked to anniversary defaults"
                            : "Unlinked - manual entry only"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            toggleFieldLink(
                              LinkFieldEnumModel.BrokerageFlatFees,
                            )
                          }
                          sx={{
                            minWidth: "32px",
                            minHeight: "32px",
                            padding: "4px",
                          }}
                        >
                          {fieldLinkStatus.brkFlatFees ? (
                            <LinkIcon
                              color="primary"
                              sx={{ fontSize: "18px" }}
                            />
                          ) : (
                            <LinkOffIcon
                              color="action"
                              sx={{ fontSize: "18px" }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="brokerageTotalFees"
          control={control}
          render={({ field }) => (
            <TextField
              type="number"
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              label="Brokerage Total Fees"
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "#fff9e6",
                },
              }}
              helperText="(auto-calculated and can't be modified)"
              // value={brokerageTotalFees?.toFixed(2) ?? ""}
              // value={
              //   typeof brokerageTotalFees === "number" &&
              //   !isNaN(brokerageTotalFees)
              //     ? brokerageTotalFees.toFixed(2)
              //     : ""
              // }
              value={(brokerageTotalFees || 0).toFixed(2)}
              onKeyDown={handleNumberInputKeyDown}
              slotProps={{
                input: {
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>

      {/* Commission Splits */}
      {/* <Grid item xs={12}>
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" gutterBottom>
          Commission Splits
        </Typography>
      </Grid> */}
      <Grid item xs={12}>
        <Divider sx={{ my: 3 }} />
        <Grid item xs={12} md={4}>
          <Controller
            name="netCommissionIncome"
            control={control}
            render={({ field }) => (
              <TextField
                id="netCommissionIncome"
                type="number"
                onWheel={(e) =>
                  e.target instanceof HTMLElement && e.target.blur()
                }
                label="Net Commission Income"
                fullWidth
                helperText="(auto-calculated and can't be modified)"
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: "#fff9e6",
                  },
                }}
                // value={netCommissionIncome?.toFixed(2) ?? ""}
                // value={
                //   typeof netCommissionIncome === "number" &&
                //   !isNaN(netCommissionIncome)
                //     ? netCommissionIncome.toFixed(2)
                //     : ""
                // }
                value={(netCommissionIncome || 0).toFixed(2)}
                onKeyDown={handleNumberInputKeyDown}
                slotProps={{
                  input: {
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Charges & Incentives */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          Other
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <Controller
          name="incentivesPaid"
          control={control}
          render={({ field }) => (
            <TextField
              id="incentivesPaid"
              type="number"
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              {...field}
              value={field.value ?? 0}
              fullWidth
              label="Expenses Paid Out of Pocket"
              helperText="Rough estimate of out-of-pocket expenses for this transaction.
Examples: Staging, photography, open houses, ads, etc."
              onKeyDown={handleNumberInputKeyDown}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>

      {/* Net Calculations */}
      {/* <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          Net Calculations
        </Typography>
      </Grid> */}

      <Grid item xs={12} md={4}>
        {/* <FormControl fullWidth variant="filled">
            <InputLabel htmlFor="netIncome">Net Income</InputLabel> */}
        <Controller
          name="netIncome"
          control={control}
          render={({ field }) => (
            <TextField
              id="netIncome"
              type="number"
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              label="Net Income"
              fullWidth
              // value={netIncome?.toFixed(2) ?? ""}
              // value={
              //   typeof netIncome === "number" && !isNaN(netIncome)
              //     ? netIncome.toFixed(2)
              //     : ""
              // }
              value={(netIncome || 0).toFixed(2)}
              onKeyDown={handleNumberInputKeyDown}
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "#fff9e6",
                },
              }}
              helperText="(auto-calculated and can't be modified)"
              slotProps={{
                input: {
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        {/* <FormControl fullWidth variant="filled">
            <InputLabel htmlFor="netIncomePercentage">Net Income</InputLabel> */}
        <Controller
          name="netIncomePercentage"
          control={control}
          render={({ field }) => (
            <TextField
              id="netIncomePercentage"
              type="number"
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
              label="Net Income %"
              fullWidth
              // value={netIncomePercentage.toFixed(2) ?? ""}
              // value={
              //   typeof netIncomePercentage === "number" &&
              //   !isNaN(netIncomePercentage)
              //     ? netIncomePercentage.toFixed(2)
              //     : ""
              // }
              value={(netIncomePercentage || 0).toFixed(2)}
              helperText="(auto-calculated and can't be modified)"
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "#fff9e6",
                },
              }}
              onKeyDown={handleNumberInputKeyDown}
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 100.0,
                  step: 0.1,
                },
                input: {
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
    </Grid>
  );
}
